import { useCallback } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { AssetForTaxSitus } from '@/modules/entities/details/shared/IntegratedAssetTaxSitusCell';
import { useUpdateEntitiesMutation } from '@/modules/entities/graphql/UpdateEntities.generated';
import { AssetTaxSitusFormValue } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import {
  buildAssetStaticSitusPatch,
  decodeSitusKind,
  encodeAssetTaxSitusFormValue,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.utils';
import {
  getAssetTaxSitusEventAction,
  useTrackAssetTaxSitusEvent,
} from '@/modules/entities/StructuredAssetsSubform/useTrackAssetTaxSitusEvent';
import { EntityType } from '@/modules/entities/types/EntityType';
import { makeEntityTypeUniversalUpdateInput } from '@/modules/entities/utils/makeEntityTypeUniversalUpdateInput';
import { useUnguardedHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AugmentedUpdateAssetV2Input } from '@/types/schema';

import { Operations as EntityDetailOperations } from '../graphql/EntityDetailPage.generated';

function buildAssetStaticUpdate(
  asset: AssetForTaxSitus,
  newSitus: AssetTaxSitusFormValue
): AugmentedUpdateAssetV2Input | null {
  if (!asset.assetStatic?.id) return null;
  return {
    id: asset.id,
    update: {},
    updateAssetStatic: {
      id: asset.assetStatic.id,
      update: buildAssetStaticSitusPatch(newSitus),
    },
  };
}

interface UseUpdateIntegratedAssetTaxSitusParams {
  entityId: string;
  entityType: EntityType;
  subtypeId: string;
  designerAccountId: string;
  valuationId: string;
  source?: 'integrated_assets_inline' | 'structured_assets_modal_inline';
  onCompleted?: () => void;
}

export function useUpdateIntegratedAssetTaxSitus({
  entityId,
  entityType,
  subtypeId,
  designerAccountId,
  valuationId,
  source = 'integrated_assets_inline',
  onCompleted,
}: UseUpdateIntegratedAssetTaxSitusParams) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const trackAssetTaxSitusEvent = useTrackAssetTaxSitusEvent();
  const { householdId } = useUnguardedHouseholdDetailsContext();
  const [updateEntities, { loading }] = useUpdateEntitiesMutation({
    refetchQueries: [EntityDetailOperations.Query.EntityDetails],
    awaitRefetchQueries: true,
  });

  const handleChange = useCallback(
    async (asset: AssetForTaxSitus, nextSitus: AssetTaxSitusFormValue) => {
      const previousSitus = encodeAssetTaxSitusFormValue(asset.assetStatic);
      if (previousSitus === nextSitus) return;
      const updateAsset = buildAssetStaticUpdate(asset, nextSitus);
      if (!updateAsset) return;

      const input = makeEntityTypeUniversalUpdateInput(entityId, entityType, {
        id: subtypeId,
        update: {},
        updateDesignerAccount: {
          id: designerAccountId,
          update: {},
          updateValuations: [
            {
              id: valuationId,
              update: {},
              updateAssets: [updateAsset],
            },
          ],
        },
      });

      try {
        await updateEntities({ variables: { input: [input] } });
        const action = getAssetTaxSitusEventAction(previousSitus, nextSitus);
        if (action) {
          trackAssetTaxSitusEvent(action, {
            source,
            householdId,
            entityId,
            assetClass: asset.class.displayName,
            situsKind: decodeSitusKind(nextSitus),
            entityType,
          });
        }
        onCompleted?.();
      } catch (err) {
        reportError(
          'Failed to update tax situs on integrated asset',
          err as Error
        );
        showFeedback('Could not save tax situs. Please try again.');
      }
    },
    [
      entityId,
      entityType,
      subtypeId,
      designerAccountId,
      valuationId,
      source,
      householdId,
      updateEntities,
      onCompleted,
      reportError,
      showFeedback,
      trackAssetTaxSitusEvent,
    ]
  );

  return { handleChange, loading };
}
